// Prose track-changes. For essays/contracts, a line-anchored diff breaks down
// when paragraphs reflow, so we diff the whole document at the word level and
// render a single flowing "track changes" block (old struck, new highlighted).
import { diffWordsWithSpace } from 'diff';
import { rawLineRows } from './line-diff.js';

const wordCount = (s) => (s.trim() ? s.trim().split(/\s+/).length : 0);

// Heuristic: sentence-like, long-ish lines, few code/structural symbols.
export function isProse(text) {
  const t = (text || '').trim();
  if (t.length < 60) return false;
  const lines = t.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return false;
  const avgLen = lines.reduce((a, l) => a + l.length, 0) / lines.length;
  const symbols = (t.match(/[{}[\]<>=;|]/g) || []).length;
  const sentences = (t.match(/[.!?]["')]?(\s|$)/g) || []).length;
  return avgLen > 40 && sentences >= 2 && symbols / t.length < 0.02;
}

export function looksLikeProse(left, right) {
  return isProse(left) && isProse(right);
}

export function diffProse(payload = {}) {
  const { left = '', right = '' } = payload;
  const spans = diffWordsWithSpace(left, right).map((p) => ({
    text: p.value,
    type: p.added ? 'new' : p.removed ? 'old' : 'ctx',
  }));

  let added = 0;
  let removed = 0;
  for (const sp of spans) {
    if (sp.type === 'new') added += wordCount(sp.text);
    else if (sp.type === 'old') removed += wordCount(sp.text);
  }

  // A removed run immediately followed by an added run reads as a replacement.
  const changes = [];
  let replaced = 0;
  for (let i = 0; i < spans.length; i++) {
    const cur = spans[i];
    const next = spans[i + 1];
    if (cur.type === 'old' && next && next.type === 'new') {
      replaced++;
      changes.push({ kind: 'change', oldText: cur.text.trim(), newText: next.text.trim() });
      i++;
    } else if (cur.type === 'old' && cur.text.trim()) {
      changes.push({ kind: 'remove', text: cur.text.trim() });
    } else if (cur.type === 'new' && cur.text.trim()) {
      changes.push({ kind: 'add', text: cur.text.trim() });
    }
  }

  // Raw view = the line-based literal diff, so the Raw switcher still works on
  // prose (the teaching fallback) instead of being inert.
  const rawRows = rawLineRows(left, right);

  return {
    format: 'prose',
    added,
    removed,
    modified: replaced,
    unchanged: 0,
    changes,
    render: { kind: 'prose', spans, rawRows },
  };
}
